import {
  Form,
  Slider,
  Typography,
  Card,
  Space,
  Empty,
  Tag,
  InputNumber,
  Tooltip,
  Button,
  Alert,
  Popconfirm,
  Collapse,
  Radio,
  Select,
  FormListFieldData,
} from "antd"
import { ReloadOutlined, LockOutlined, UnlockOutlined } from "@ant-design/icons"
import { observer } from "mobx-react"
import { COUNTRY_CODES } from "utils/countryCodes"
import type { FeatureDefinition } from "../types"

const { Title, Paragraph, Text } = Typography

type Props = {
  platform: "Desktop" | "Mobile";
  listName: "desktop" | "mobile";
  features: FeatureDefinition[];
}

export type FeatureFlagFormItem = {
  rolloutPercentage?: number;
  savedRolloutPercentage?: number;
  seed?: number;
  seedUnlocked?: boolean;
  // Advanced targeting (flat form state; assembled into nested filters on save).
  countryMode?: "none" | "whitelist" | "blacklist";
  countries?: string[];
  userType?: "none" | "premium" | "regular";
  minDays?: number;
  maxDays?: number;
}

type FeatureFlagCardProps = {
  field: FormListFieldData;
  feature: FeatureDefinition;
  listName: "desktop" | "mobile";
}

// 32-bit unsigned integer
const UINT32_MAX = 2 ** 32 - 1

const COUNTRY_OPTIONS = COUNTRY_CODES.map(({ code, name }) => ({ label: `${name} (${code})`, value: code }))

// Generate random seed for consistent user bucketing
const generateRandomSeed = (): number => {
  return Math.floor(Math.random() * UINT32_MAX)
}

const getStatusColor = (percentage: number): string => {
  if (percentage === 0) return "error" // red - disabled
  if (percentage === 100) return "success" // green - full rollout

  return "warning" // yellow - partial rollout
}

const getStatusLabel = (percentage: number): string => {
  if (percentage === 0) return "Disabled"
  if (percentage === 100) return "Full Rollout"

  return "Partial Rollout"
}

const FeatureFlagCard = ({ field, feature, listName }: FeatureFlagCardProps) => {
  const form = Form.useFormInstance()

  const rolloutPercentage = Form.useWatch([listName, field.name, "rolloutPercentage"], form) ?? 0
  const savedRolloutPercentage = Form.useWatch([listName, field.name, "savedRolloutPercentage"], form) ?? 0
  const seedUnlockedValue = Form.useWatch([listName, field.name, "seedUnlocked"], form)
  const seedUnlocked = seedUnlockedValue ?? (savedRolloutPercentage === 0)

  const countryMode = Form.useWatch([listName, field.name, "countryMode"], form) ?? "none"

  const updateField = (patch: Partial<FeatureFlagFormItem>) => {
    const current = form.getFieldValue([listName, field.name]) ?? {}
    form.setFields([{ name: [listName, field.name], value: { ...current, ...patch } }])
  }

  return (
    <Card
      title={
        <Space style={{ width: "100%", justifyContent: "space-between" }}>
          <Text strong>{feature.displayName}</Text>
          <Tag color={getStatusColor(rolloutPercentage)}>
            {rolloutPercentage}% • {getStatusLabel(rolloutPercentage)}
          </Tag>
        </Space>
      }
    >
      <Space direction="vertical" style={{ width: "100%" }}>
        <div>
          <Text strong>Feature ID: </Text>
          <Text code>{feature.id}</Text>
        </div>

        {feature.description && (
          <Paragraph type="secondary" style={{ marginBottom: 16 }}>
            {feature.description}
          </Paragraph>
        )}

        <Form.Item
          name={[field.name, "rolloutPercentage"]}
          label="Rollout Percentage"
          rules={[{ required: true, type: "number", min: 0, max: 100 }]}
        >
          <Slider
            min={0}
            max={100}
            step={1}
            marks={{
              0: "0%",
              10: "10%",
              20: "20%",
              30: "30%",
              40: "40%",
              50: "50%",
              60: "60%",
              70: "70%",
              80: "80%",
              90: "90%",
              100: "100%",
            }}
            tooltip={{ formatter: (value: number | undefined) => `${value ?? 0}%` }}
          />
        </Form.Item>

        <Form.Item
          label={
            <Tooltip
              title={
                "Controls deterministic user bucketing for this feature. "
                + "Users with the same seed value will be bucketed consistently."
              }
            >
              <span>Bucketing Seed</span>
            </Tooltip>
          }
          required
          colon={false}
        >
          {savedRolloutPercentage > 0 && (
            <Alert
              style={{ marginBottom: 8 }}
              type="warning"
              showIcon
              message="Changing seed during rollout can disrupt user groups"
              description={
                "The seed determines which users are included in this feature. "
                + "Changing it while rollout > 0 will re-bucket users, affecting A/B groups, "
                + "metrics continuity, and user experience consistency."
              }
            />
          )}
          <Space.Compact style={{ width: "100%" }}>
            {!seedUnlocked ? (
              <Popconfirm
                title={
                  <span>
                    Changing the seed while a rollout is in progress reshuffles which users get this feature.
                    Are you sure you want to continue?
                  </span>
                }
                okText="Unlock"
                okType="danger"
                onConfirm={() => updateField({ seedUnlocked: true })}
              >
                <Button icon={<LockOutlined />} danger>
                  Unlock
                </Button>
              </Popconfirm>
            ) : (
              <Button
                icon={<UnlockOutlined />}
                onClick={() => updateField({ seedUnlocked: false })}
              >
                Lock
              </Button>
            )}
            <Form.Item
              name={[field.name, "seed"]}
              rules={[{ required: true, message: "Seed is required" }]}
              noStyle
            >
              <InputNumber
                style={{ width: "100%" }}
                placeholder="Enter seed value"
                min={0}
                max={UINT32_MAX}
                step={1}
                precision={0}
                disabled={!seedUnlocked}
              />
            </Form.Item>
            <Button
              icon={<ReloadOutlined />}
              disabled={!seedUnlocked}
              onClick={() => updateField({ seed: generateRandomSeed() })}
            >
              Random
            </Button>
          </Space.Compact>
        </Form.Item>

        <Collapse ghost>
          <Collapse.Panel header="Advanced Targeting" key="advanced">
            <Space direction="vertical" style={{ width: "100%" }} size="middle">
              <Text type="secondary">
                Optional filters applied in addition to the rollout percentage (all must pass). Only
                evaluated for logged-in users.
              </Text>

              <Form.Item name={[field.name, "countryMode"]} label="Country" style={{ marginBottom: 0 }}>
                <Radio.Group>
                  <Radio value="none">None</Radio>
                  <Radio value="whitelist">Whitelist</Radio>
                  <Radio value="blacklist">Blacklist</Radio>
                </Radio.Group>
              </Form.Item>

              {countryMode !== "none" && (
                <Form.Item
                  name={[field.name, "countries"]}
                  label="Countries (ISO alpha-2)"
                  rules={[
                    {
                      validator: (_, value) => Array.isArray(value) && value.length > 0
                        ? Promise.resolve()
                        : Promise.reject(new Error("Select at least one country, or set Country to None")),
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    placeholder="Select countries"
                    options={COUNTRY_OPTIONS}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              )}

              <Form.Item name={[field.name, "userType"]} label="User type" style={{ marginBottom: 0 }}>
                <Radio.Group>
                  <Radio value="none">None</Radio>
                  <Radio value="premium">Premium only</Radio>
                  <Radio value="regular">Regular only</Radio>
                </Radio.Group>
              </Form.Item>

              <Form.Item label="Account age (days)" style={{ marginBottom: 0 }}>
                <Space>
                  <Form.Item name={[field.name, "minDays"]} label="Min" rules={[{ type: "number", min: 0 }]}>
                    <InputNumber min={0} precision={0} placeholder="Min days" />
                  </Form.Item>
                  <Form.Item
                    name={[field.name, "maxDays"]}
                    label="Max"
                    dependencies={[[listName, field.name, "minDays"]]}
                    rules={[
                      { type: "number", min: 0 },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const min = getFieldValue([listName, field.name, "minDays"])
                          if (typeof value === "number" && typeof min === "number" && value <= min) {
                            return Promise.reject(new Error("Max days must be greater than Min days"))
                          }

                          return Promise.resolve()
                        },
                      }),
                    ]}
                  >
                    <InputNumber min={0} precision={0} placeholder="Max days" />
                  </Form.Item>
                </Space>
              </Form.Item>
            </Space>
          </Collapse.Panel>
        </Collapse>
      </Space>
    </Card>
  )
}

export const FeatureFlagsFields = observer(({ platform, listName, features }: Props) => (
  <div style={{ maxWidth: 900 }}>
    <Title level={2}>{platform} Feature Flags</Title>
    <Paragraph>
      Control the rollout percentage for each {platform.toLowerCase()} feature.
      Set to 0% to disable completely, or 100% to enable for all users.
    </Paragraph>

    {features.length === 0 ? (
      <Empty
        description={
          <Space direction="vertical" size="small">
            <Text strong>No {platform.toLowerCase()} features configured yet</Text>
            <Text type="secondary">
              Add features to the <Text code>{platform.toLowerCase()}Features.ts</Text> file to enable rollout control
            </Text>
          </Space>
        }
        style={{ margin: "48px 0" }}
      />
    ) : (
      <Form.List name={listName}>
        {fields => (
          <Space direction="vertical" style={{ width: "100%", marginBottom: 24 }} size="large">
            {fields.map((field, index) => {
              const feature = features[index]
              if (!feature) return null

              return (
                <FeatureFlagCard
                  key={feature.id}
                  field={field}
                  feature={feature}
                  listName={listName}
                />
              )
            })}
          </Space>
        )}
      </Form.List>
    )}
  </div>
))
